// Cliente Supabase para el flujo de postulación.
// Las llamadas a la tabla `recruitment` se hacen desde aquí.
//
// Schema de la tabla está en sql/recruitment_schema.sql (espejo del modelo
// Mongoose de Lemon en camelCase). Cuando Lemon migre, los campos coinciden 1:1.
//
// Cada visitante recibe una sesión anónima (Supabase Anonymous Auth) y la
// columna `applicant_uid` linkea cada fila a esa sesión. RLS solo permite
// ver/actualizar la propia fila aunque la anon key sea pública.

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
// Aceptamos los 2 nombres comunes para la key (publishable o anon).
// Vercel/local pueden usar cualquiera de los dos según preferencia del operador.
const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_KEY) {
  // Sin credenciales el cliente queda inactivo. La app sigue funcionando
  // (los saves a Supabase fallan en silencio y los datos viven en localStorage).
  // eslint-disable-next-line no-console
  console.warn('[supabase] Faltan REACT_APP_SUPABASE_URL o REACT_APP_SUPABASE_PUBLISHABLE_KEY en .env.local');
}

export const supabase =
  SUPABASE_URL && SUPABASE_KEY ? createClient(SUPABASE_URL, SUPABASE_KEY) : null;

const TABLE = 'recruitment';

// Storage key para el ID del registro en localStorage (permite resumir un draft)
const APPLICANT_ID_KEY = 'impekble_applicant_id';

export function getStoredApplicantId() {
  try {
    return localStorage.getItem(APPLICANT_ID_KEY) || null;
  } catch (_) {
    return null;
  }
}

export function setStoredApplicantId(id) {
  try {
    if (id) localStorage.setItem(APPLICANT_ID_KEY, id);
    else localStorage.removeItem(APPLICANT_ID_KEY);
  } catch (_) {
    // localStorage puede estar bloqueado en modo privado; ignorar
  }
}

// Garantiza que haya una sesión anónima activa. Si no existe, la crea.
// Devuelve el `auth.uid()` de la sesión, o null si Supabase no responde.
async function ensureAnonSession() {
  if (!supabase) return null;

  const { data: { session } } = await supabase.auth.getSession();
  if (session?.user?.id) return session.user.id;

  const { data, error } = await supabase.auth.signInAnonymously();
  if (error) {
    // eslint-disable-next-line no-console
    console.error('[supabase] signInAnonymously error', error);
    return null;
  }
  return data?.session?.user?.id || null;
}

/**
 * Crea un registro en `recruitment` con los datos del paso 1 del formulario.
 * Devuelve el ID generado y lo guarda en localStorage para futuros updates.
 *
 * Si Supabase no está configurado o falla, devuelve null silenciosamente —
 * la app sigue funcionando offline-first (los datos viven en localStorage).
 */
export async function createApplicant(form) {
  if (!supabase) return null;

  const applicantUid = await ensureAnonSession();
  if (!applicantUid) return null;

  const payload = {
    applicant_uid: applicantUid,

    // Datos personales (paso 1)
    name: form.name?.trim() || null,
    email: form.email?.trim() || null,
    phoneNumber: form.phoneNumber ? Number(form.phoneNumber.replace(/\D/g, '')) || null : null,
    nationalId: form.nationalId?.trim() || null,
    birthDate: form.birthDate || null,
    locationAddress: form.region?.trim() || null,
    aboutMe: form.aboutMe?.trim() || null,
    recruitmentRole: form.recruitmentRole || null,

    // Defaults del modelo Lemon
    recruitmentType: 'online application',
    status: 'pending',
    fullName: form.name?.trim() || null,
    nationality: 'CL',

    // Funnel y consentimiento
    funnelStatus: 'form_done',
    dataConsentAccepted: !!form.dataConsentAccepted,
    dataConsentAt: form.dataConsentAccepted ? new Date().toISOString() : null,
  };

  try {
    const { data, error } = await supabase
      .from(TABLE)
      .insert([payload])
      .select('id')
      .single();

    if (error) {
      // eslint-disable-next-line no-console
      console.error('[supabase] insert error', error);
      return null;
    }

    setStoredApplicantId(data.id);
    return data.id;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[supabase] insert exception', e);
    return null;
  }
}

/**
 * Actualiza el registro `recruitment` con los campos pasados.
 * Usa el ID guardado en localStorage. Si no hay ID, sesión expirada
 * o Supabase está desactivado, devuelve null silenciosamente.
 *
 * RLS garantiza que sólo se actualice la fila vinculada a la sesión
 * anónima activa, aunque el cliente tenga un id de otro postulante.
 */
export async function updateApplicant(patch) {
  if (!supabase) return null;
  const id = getStoredApplicantId();
  if (!id) return null;

  // Garantiza que la sesión anónima siga viva (default 1h, puede expirar
  // entre el paso 1 y el quiz si el postulante demora). Si expiró, se
  // crea una nueva — pero será un `auth.uid()` distinto y RLS bloqueará
  // el update. En ese caso la app degrada a localStorage-only.
  const applicantUid = await ensureAnonSession();
  if (!applicantUid) return null;

  try {
    const { error } = await supabase
      .from(TABLE)
      .update(patch)
      .eq('id', id);

    if (error) {
      // eslint-disable-next-line no-console
      console.error('[supabase] update error', error);
      return null;
    }
    return id;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[supabase] update exception', e);
    return null;
  }
}

/**
 * Helpers semánticos por momento del flujo.
 */
export async function markVideoIntroWatched() {
  return updateApplicant({
    videoIntroWatched: true,
    videoIntroWatchedAt: new Date().toISOString(),
    funnelStatus: 'video_watched',
  });
}

export async function markStage1Started() {
  return updateApplicant({
    quizStage1StartedAt: new Date().toISOString(),
  });
}

export async function markStage1Done({ answers, score }) {
  return updateApplicant({
    quizStage1Answers: answers || {},
    quizStage1Score: typeof score === 'number' ? score : null,
    quizStage1FinishedAt: new Date().toISOString(),
    funnelStatus: 'stage1_done',
  });
}
